import React from 'react';
import {
  TableRow, TableCell, Chip, IconButton, Box, Typography,
  Card, CardActionArea, CardContent,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArticleIcon from '@mui/icons-material/Article';
// 3.2h · Office 檔型用 Font Awesome file icons（Simple Icons 已移除 Word/Excel/PPT 商標圖），
// 套官方品牌色以利辨識。
import { FaFileWord, FaFileExcel, FaFilePowerpoint } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import { DriveFile } from '../services/myDriveService';
import { ViewMode } from './Toolbar';
import { mimeLabel } from '../utils/mimeLabel';
import { formatLocalMinute } from '../../../../utils/dateHelper';

// 3.2h · 純函式分類器（可單測，不需 render）：mime → 圖示種類。
export type IconKind = 'folder' | 'pdf' | 'image' | 'word' | 'excel' | 'ppt' | 'office' | 'file';

export function classifyIcon(file: { is_directory: boolean; mime_type?: string | null }): IconKind {
  if (file.is_directory) return 'folder';
  const m = file.mime_type || '';
  if (m === 'application/pdf') return 'pdf';
  if (m.startsWith('image/')) return 'image';
  if (m.includes('wordprocessingml') || m === 'application/msword') return 'word';
  if (m.includes('spreadsheetml') || m === 'application/vnd.ms-excel') return 'excel';
  if (m.includes('presentationml') || m === 'application/vnd.ms-powerpoint') return 'ppt';
  if (m.includes('officedocument') || m.includes('msword')) return 'office';
  return 'file';
}

// 品牌色：Word 藍 / Excel 綠 / PPT 橘（官方色）。fa 圖示 22px 對齊 MUI 24px 行高。
const _FA_STYLE = { verticalAlign: 'middle' as const };

function getIcon(file: DriveFile) {
  switch (classifyIcon(file)) {
    case 'folder': return <FolderIcon color="warning" />;
    case 'pdf': return <PictureAsPdfIcon color="error" />;
    case 'image': return <ImageIcon color="info" />;
    case 'word': return <FaFileWord color="#2B579A" size={22} style={_FA_STYLE} />;
    case 'excel': return <FaFileExcel color="#217346" size={22} style={_FA_STYLE} />;
    case 'ppt': return <FaFilePowerpoint color="#D24726" size={22} style={_FA_STYLE} />;
    case 'office': return <ArticleIcon color="primary" />;
    default: return <InsertDriveFileIcon />;
  }
}

function formatSize(bytes: number) {
  if (bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

interface Props {
  file: DriveFile;
  selected: boolean;
  readonly: boolean;
  view: ViewMode;
  onSingleClick: (ev: React.MouseEvent) => void;
  onDoubleClick: () => void;
  onContextMenu: (ev: React.MouseEvent) => void;
  onPreview: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onDragStart: (ev: React.DragEvent) => void;
  onDragOver: (ev: React.DragEvent) => void;
  onDrop: (ev: React.DragEvent) => void;
}

export default function FileItem({
  file, selected, readonly, view,
  onSingleClick, onDoubleClick, onContextMenu, onPreview, onDownload, onDelete,
  onDragStart, onDragOver, onDrop,
}: Props) {
  const { t } = useTranslation();

  const scanChip = (
    <Chip
      size="small"
      label={t(`myDrive.scan${file.scan_status.charAt(0).toUpperCase() + file.scan_status.slice(1)}`,
               file.scan_status)}
      color={file.scan_status === 'clean' ? 'success' : file.scan_status === 'infected' ? 'error' : 'default'}
    />
  );

  if (view === 'grid') {
    return (
      <Card
        variant="outlined"
        sx={{
          width: 120,
          m: 0.5,
          outline: selected ? '2px solid' : undefined,
          outlineColor: 'primary.main',
          cursor: 'pointer',
        }}
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onContextMenu={onContextMenu}
      >
        <CardActionArea
          onClick={onSingleClick}
          onDoubleClick={onDoubleClick}
          sx={{ p: 1, textAlign: 'center' }}
        >
          <Box sx={{ fontSize: 40, display: 'flex', justifyContent: 'center' }}>
            {getIcon(file)}
          </Box>
          <CardContent sx={{ p: '4px !important' }}>
            <Typography variant="caption" noWrap title={file.filename}>
              {file.filename}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {formatSize(file.size_bytes)}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  return (
    <TableRow
      hover
      selected={selected}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onSingleClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell sx={{ width: 32, p: 0.5 }}>{getIcon(file)}</TableCell>
      <TableCell>{file.filename}</TableCell>
      <TableCell>{formatSize(file.size_bytes)}</TableCell>
      <TableCell>{mimeLabel(file.mime_type)}</TableCell>
      <TableCell>{formatLocalMinute(file.updated_at)}</TableCell>
      <TableCell>{scanChip}</TableCell>
      <TableCell align="right" onClick={e => e.stopPropagation()}>
        {!file.is_directory && (
          <IconButton size="small" onClick={onPreview}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        )}
        {!file.is_directory && (
          <IconButton size="small" onClick={onDownload}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        )}
        {!readonly && (
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}
